import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Card, Chip, Paper, TextField, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, Stack, Dialog, DialogTitle, DialogContent,
  DialogActions, MenuItem, Select, InputAdornment, FormControl, InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon, Store as StoreIcon,
} from '@mui/icons-material';
import axios from '../api';
import PaginationControls from '../components/PaginationControls';

// Support tickets can be open / in-progress / resolved / closed. Keep the
// label + color mapping in one place so the table, the detail dialog and the
// filter all render every status consistently (previously "in-progress" was
// mislabelled as "Resolved" by a binary open/resolved check).
type TicketStatus = 'open' | 'in-progress' | 'resolved' | 'closed';
const STATUS_META: Record<TicketStatus, { label: string; color: 'warning' | 'info' | 'success' | 'default' }> = {
  open: { label: 'Open', color: 'warning' },
  'in-progress': { label: 'In Progress', color: 'info' },
  resolved: { label: 'Resolved', color: 'success' },
  closed: { label: 'Closed', color: 'default' },
};
const statusMeta = (status?: string) =>
  STATUS_META[status as TicketStatus] || { label: status || 'Unknown', color: 'default' as const };
// A ticket still needs attention (can be responded to / resolved) while it is
// open or in-progress.
const isActionable = (status?: string) => status === 'open' || status === 'in-progress';

export default function Support() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [response, setResponse] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [serverStats, setServerStats] = useState<{ total: number; open: number; inProgress: number; resolved: number; merchant: number; customer: number } | null>(null);

  useEffect(() => {
    const requestedTicket = searchParams.get('ticket');
    if (!requestedTicket || loading) return;

    const ticket = tickets.find((item) => item.ticketNumber === requestedTicket || item.id === requestedTicket);
    if (ticket) {
      setSelectedTicket(ticket);
      setDialogOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [loading, searchParams, setSearchParams, tickets]);

  const fetchTickets = useCallback(async () => {
    try {
      const response = await axios.get('/api/operations/support/tickets', { params: { page, limit: rowsPerPage } });
      setTotal(response.data.total ?? response.data.tickets?.length ?? 0);
      setServerStats(response.data.stats || null);
      setTickets((response.data.tickets || []).map((ticket: any) => ({
        ...ticket,
        id: ticket._id || ticket.id,
        responses: Array.isArray(ticket.responses) ? ticket.responses : [],
      })));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.ticketNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'All' || ticket.type === typeFilter;
    const matchesStatus = statusFilter === 'All' || ticket.status === statusFilter;
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleOpenTicket = (ticket: any) => {
    setSelectedTicket(ticket);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setResponse('');
  };

  const handleResolveTicket = async () => {
    if (selectedTicket) {
      try {
        const result = await axios.put(`/api/operations/support/tickets/${selectedTicket.id}/resolve`);
        const updatedTicket = { ...result.data.ticket, id: result.data.ticket._id || selectedTicket.id };
        setTickets(current => current.map(ticket => ticket.id === selectedTicket.id ? updatedTicket : ticket));
        setSelectedTicket(updatedTicket);
      } catch (error) {
        console.error('Error resolving ticket:', error);
      }
    }
  };

  const handleSendResponse = async () => {
    if (!response.trim() || !selectedTicket) return;

    try {
      const result = await axios.post(`/api/operations/support/tickets/${selectedTicket.id}/response`, {
        message: response.trim(),
      });
      const updatedTicket = { ...result.data.ticket, id: result.data.ticket._id || selectedTicket.id };
      setTickets(current => current.map(ticket => ticket.id === selectedTicket.id ? updatedTicket : ticket));
      setSelectedTicket(updatedTicket);
      setResponse('');
    } catch (error) {
      console.error('Error sending support response:', error);
    }
  };

  // Prefer the backend's platform-wide counts (the API already computes
  // these via aggregate) so the cards don't undercount once there's more
  // than one page of tickets.
  const stats = serverStats || {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in-progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    merchant: tickets.filter(t => t.type === 'merchant').length,
    customer: tickets.filter(t => t.type === 'customer').length,
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Support Tickets</Typography>
        <Typography color="text.secondary">Manage customer and merchant support inquiries.</Typography>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(6, 1fr)' }, gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Total Tickets</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>{stats.total}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Open</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>{stats.open}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">In Progress</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>{stats.inProgress}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">Resolved</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{stats.resolved}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">From Customers</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main' }}>{stats.customer}</Typography>
        </Paper>
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary" variant="body2">From Merchants</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'secondary.main' }}>{stats.merchant}</Typography>
        </Paper>
      </Box>

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, p: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search tickets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
              {['All', 'customer', 'merchant'].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt === 'All' ? 'All' : opt === 'customer' ? 'Customer' : 'Merchant'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              {['All', 'open', 'in-progress', 'resolved', 'closed'].map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt === 'All' ? 'All' : statusMeta(opt).label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Card>

      {/* Tickets Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.default' }}>
                <TableCell><strong>Ticket ID</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>User</strong></TableCell>
                <TableCell><strong>Subject</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Priority</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell><strong>Responses</strong></TableCell>
                <TableCell><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography>Loading tickets...</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredTickets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">No tickets found.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredTickets.length > 0 && (
                filteredTickets.map((ticket) => (
                  <TableRow key={ticket.id} hover>
                    <TableCell><Typography sx={{ fontWeight: 600 }}>{ticket.ticketNumber}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        icon={ticket.type === 'customer' ? <PersonIcon /> : <StoreIcon />}
                        label={ticket.type === 'customer' ? 'Customer' : 'Merchant'}
                        size="small"
                        color={ticket.type === 'customer' ? 'info' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{ticket.userName}</TableCell>
                    <TableCell>{ticket.subject}</TableCell>
                    <TableCell>
                      <Chip
                        label={statusMeta(ticket.status).label}
                        color={statusMeta(ticket.status).color}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ticket.priority}
                        color={ticket.priority === 'high' ? 'error' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{new Date(ticket.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="center">{ticket.responses.length}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleOpenTicket(ticket)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <PaginationControls page={page} total={total} rowsPerPage={rowsPerPage} onPageChange={setPage} onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }} />

      {/* Ticket Detail Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{selectedTicket?.ticketNumber}</Typography>
            <Chip
              label={statusMeta(selectedTicket?.status).label}
              color={statusMeta(selectedTicket?.status).color}
              size="small"
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">Type</Typography>
              <Chip
                icon={selectedTicket?.type === 'customer' ? <PersonIcon /> : <StoreIcon />}
                label={selectedTicket?.type === 'customer' ? 'Customer' : 'Merchant'}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">User</Typography>
              <Typography sx={{ fontWeight: 600 }}>{selectedTicket?.userName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Subject</Typography>
              <Typography sx={{ fontWeight: 600 }}>{selectedTicket?.subject}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">Message</Typography>
              <Paper sx={{ p: 2, bgcolor: 'action.hover', mt: 1 }}>
                <Typography variant="body2">{selectedTicket?.message}</Typography>
              </Paper>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Conversation</Typography>
              <Paper
                sx={{
                  p: 2,
                  bgcolor: '#f4f7f5',
                  maxHeight: 280,
                  overflowY: 'auto',
                }}
              >
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Box sx={{ maxWidth: '82%', bgcolor: 'white', borderRadius: 2, px: 2, py: 1.25, boxShadow: 1 }}>
                      <Typography variant="caption" color="text.secondary">{selectedTicket?.userName}</Typography>
                      <Typography variant="body2">{selectedTicket?.message}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selectedTicket?.createdAt ? new Date(selectedTicket.createdAt).toLocaleString() : ''}
                      </Typography>
                    </Box>
                  </Box>
                  {(selectedTicket?.responses || []).map((item: any, index: number) => (
                    <Box key={`${item.createdAt || 'response'}-${index}`} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Box sx={{ maxWidth: '82%', bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2, px: 2, py: 1.25 }}>
                        <Typography variant="caption">Admin</Typography>
                        <Typography variant="body2">{item.message}</Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                          {item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Paper>
            </Box>
            {isActionable(selectedTicket?.status) && (
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Type your response..."
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                size="small"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {isActionable(selectedTicket?.status) && (
            <>
              <Button onClick={() => handleSendResponse()} variant="contained" size="small">
                Send Response
              </Button>
              <Button onClick={handleResolveTicket} variant="outlined" size="small" color="success">
                Resolve Ticket
              </Button>
            </>
          )}
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
